import React, { Component } from 'react'
import moment from 'moment'

import {
  Theme,
  Typography,
  LinearProgress,
  Button,
  Snackbar,
} from '@material-ui/core'
import { WithStyles, createStyles, withStyles } from '@material-ui/styles'

import { AutoSizer, MultiGrid as VirtualizedMultiGrid } from 'react-virtualized'
import 'react-virtualized/styles.css'

import { ProductionCode, TelemetryType } from 'api/apiservice'
import {
  celsiusToFahrenheit,
  feetToBbl,
  feetToFeetAndInches,
  getReadableTelemetryType,
  getTelemetryTypeUnit,
  inchToBbl,
  inchToFeet,
} from 'components/utils/converter'
import { HistoricalDataByTelemetryType } from 'store/HistoricalData'
import { ModelState } from 'store/ModelState'
import memoize from 'memoize-one'
import { HistoricalDataEntry, updateTelemetryDatapoint } from '../../api/client'
import InvalidDatapointDialog from '../details/InvalidData/InvalidDatapoint'
import { AppState } from '../../store/AppState'
import { connect } from 'react-redux'
import {
  hasSupervisorUserAccessLevel,
  isAdmin,
  isSystemAdmin,
} from 'store/oidc/userManager'
import { MultitenantUserState } from '../../store/reducers/multitenant'
import { Asset, Site, Tank } from 'store/Site'
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert'

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      height: '100%',
      width: '100%',
      display: 'grid',
      alignItems: 'flex-start',
      alignSelf: 'flex-start',
      marginBottom: 'auto',
      [theme.breakpoints.up('md')]: {
        width: '40vw',
      },
    },
    cell: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '0.5em',
      textAlign: 'center',
      borderBottom: '1px solid #e0e0e0',
    },
    cellInvalid: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '0.5em',
      textAlign: 'center',
      borderBottom: '1px solid #e0e0e0',
      color: 'red',
    },
    noCells: {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1em',
      color: '#bdbdbd',
    },
  })

const GreyTextTypography = withStyles({
  root: {
    color: '#999999',
  },
})(Typography)

const rowNamesGasFlow = [
  'Accumulated Volume',
  'Battery Volts',
  'Diff Pressure',
  'Static Pressure',
  'Temperature',
  'Today Volume Flowed',
  'Volume Flow Rate',
  'Yesterday Volume Flowed',
]

const rowNamesTankLevel = [
  'Tank Level',
  'Edit',
  'Rate Of Change (BBL)',
  'Rate Of Change (Feet)',
]

const rowNamesChemicalTankLevel = ['Volume']

interface Props extends ModelState {
  readonly allHistoricalData: HistoricalDataByTelemetryType
  hideData: string[]
  readonly fullWidth?: boolean
  readonly onOpenDatapointInfo?: (currentData) => void
  readonly updateState: (id, validData) => string[]
  readonly site?: Site
  readonly asset?: Asset
}

interface PropsFromState {
  readonly user: MultitenantUserState
  readonly accessToken?: string
  readonly tenantId: number
  readonly email: string
}

interface CurrentData extends ModelState {
  readonly currData?: HistoricalDataEntry
  readonly showModal: boolean
  readonly updatedInfo: boolean
  readonly hiddenData: string[]
}

type AllProps = Props & PropsFromState & WithStyles<typeof styles>

const rowHeight = 60
const fixedColumnCount = 1
const overscanColumnCount = 10
const scrollToColumn = Number.MAX_VALUE

class MeasuresTable extends Component<AllProps, CurrentData> {
  private readonly gridRef = React.createRef<VirtualizedMultiGrid>()
  private readonly memoizedData = memoize(
    (allHistoricalData: HistoricalDataByTelemetryType) => {
      const data = new Map<number, HistoricalDataEntry[]>()
      const telemetryData = Object.values(allHistoricalData)

      for (let i = 0; i < telemetryData.length; i++) {
        const telemetry = telemetryData[i]!

        telemetry.forEach((entry) => {
          const column = data.get(entry.timestamp) ?? []

          column[i] = entry

          data.set(entry.timestamp, column)
        })
      }

      const keys = Array.from(data.keys()).sort((a, b) => a - b)
      const sortedData = keys.map((k) => data.get(k))

      return sortedData
    }
  )

  constructor(props) {
    super(props)

    this.state = {
      currData: undefined,
      showModal: false,
      hiddenData: this.props.hideData,
      updatedInfo: false,
    }

    this.renderHeaderCell = this.renderHeaderCell.bind(this)
    this.cellRenderer = this.cellRenderer.bind(this)
    this.renderRowCellName = this.renderRowCellName.bind(this)
    this.getColumnWidth = this.getColumnWidth.bind(this)
    this.noContentRenderer = this.noContentRenderer.bind(this)
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  isSystemAdmin = isSystemAdmin(this.props.user.selectedTenant?.roles)
  isAdmin = isAdmin(this.props.user.selectedTenant?.roles)
  validRole = hasSupervisorUserAccessLevel(
    this.props.user.selectedTenant?.roles
  )

  public openModal = (currentData) => {
    this.setState({
      currData: currentData,
      showModal: true,
    })
  }

  public closeModal = (id: string) => (ok: boolean) => {
    if (ok) {
      const currData = this.state.currData
      this.setValidDatapoint(
        currData?.id,
        currData?.telemetryType,
        currData?.invalidData,
        currData
      )
    } else {
      this.setState({
        showModal: false,
        currData: undefined,
        updatedInfo: false,
      })
    }
  }

  async setValidDatapoint(
    id: any,
    telemetryType: TelemetryType | undefined,
    invalidData: boolean | undefined,
    currentData: HistoricalDataEntry | undefined
  ): Promise<void> {
    const { accessToken, tenantId, email } = this.props

    if (!accessToken || !tenantId) {
      return
    }

    const result = await updateTelemetryDatapoint(
      accessToken,
      tenantId,
      telemetryType,
      invalidData,
      email,
      id
    )

    let updateId = ''
    const state = !currentData?.invalidData
    if (result) {
      const currentId = currentData?.id
      const itemFound = this.state.hiddenData.findIndex((t) => t === currentId)
      if (itemFound !== undefined && itemFound !== -1) {
        updateId = currentId
      } else {
        updateId = id
      }
    }
    const currState = this.props.updateState(updateId, state)
    this.setState({
      showModal: false,
      currData: undefined,
      hiddenData: currState,
      updatedInfo: true,
    })
  }

  public render() {
    const { fullWidth, classes } = this.props

    const divStyle: React.CSSProperties | undefined = fullWidth
      ? { width: '100%' }
      : {}

    return (
      <div className={classes.container} style={divStyle}>
        {this.getMainBody()}
        {this.state.showModal && (
          <InvalidDatapointDialog
            currData={this.state.currData}
            site={this.props.site}
            onClose={this.closeModal(this.state.currData?.id)}
          />
        )}
        {this.state.updatedInfo && (
          <Snackbar
            open={true}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            style={{ zIndex: 2147483647, top: 100 }}
            onClose={() => {
              this.setState({
                updatedInfo: false,
              })
            }}
          >
            <Alert severity="warning">
              The data has changed, please{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  window.location.reload()
                }}
                style={{
                  color: '#fff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                refresh the page
              </a>
              .
            </Alert>
          </Snackbar>
        )}
      </div>
    )
  }

  private getMainBody(): React.ReactNode {
    const { loading, allHistoricalData } = this.props

    if (loading) {
      return <LinearProgress />
    }

    const historicalData = this.hideHistoricalData(
      allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)

    const currentType: TelemetryType = Number(Object.keys(historicalData)[0])

    let rowCount, columnCount

    if (currentType === TelemetryType.GasFlow) {
      const dataHistorical = historicalData[TelemetryType.GasFlow] ?? [0]

      if (dataHistorical !== undefined) {
        rowCount = 9
        columnCount = data.length + 1
      }
    } else if (currentType === TelemetryType.PumpControl) {
      const dataHistoricalArray =
        historicalData[TelemetryType.PumpControl] ?? []
      const dataHistorical = dataHistoricalArray[dataHistoricalArray.length - 1]

      if (data.length === 0) {
        rowCount = 0
        columnCount = 0
      } else if (
        dataHistorical !== undefined &&
        dataHistorical.metaData !== undefined
      ) {
        rowCount = Object.keys(dataHistorical.metaData).length + 1 // +1 for the Time row
        columnCount = data.length + 1
      } else {
        rowCount = 1
        columnCount = data.length + 1
      }
    } else if (currentType === TelemetryType.TankLevel) {
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )

      rowCount = Object.keys(historicalData).length + 4
      if (tank?.productionCode === ProductionCode.Chemical) {
        rowCount = rowCount - 1
      }
      columnCount = data.length + 1
    } else {
      rowCount = Object.keys(historicalData).length + 3
      columnCount = data.length + 1
    }

    return (
      <AutoSizer>
        {({ width, height: autoSizerHeight }) => (
          <VirtualizedMultiGrid
            {...this.state}
            ref={this.gridRef}
            width={width}
            height={autoSizerHeight}
            rowHeight={rowHeight}
            rowCount={rowCount}
            columnCount={columnCount}
            columnWidth={this.getColumnWidth.bind(this, currentType)}
            fixedColumnCount={fixedColumnCount}
            overscanColumnCount={overscanColumnCount}
            cellRenderer={this.cellRenderer}
            noContentRenderer={this.noContentRenderer}
            scrollToColumn={scrollToColumn}
            openModal={this.openModal}
            closeModal={this.closeModal}
          />
        )}
      </AutoSizer>
    )
  }

  public getHiddenData(historicalData): string[] {
    const currentTelemetry = Number(Object.keys(historicalData))
    const invalidIds = historicalData[currentTelemetry]
      .filter((x) => x.invalidData)
      .map((x) => x.id)
    return invalidIds
  }

  private hideHistoricalData(
    allHistoricalData: HistoricalDataByTelemetryType,
    hiddenData: string[]
  ): HistoricalDataByTelemetryType {
    const currentTelemetry = Number(Object.keys(allHistoricalData))
    if (
      allHistoricalData !== undefined &&
      allHistoricalData[currentTelemetry] !== undefined
    ) {
      allHistoricalData[currentTelemetry].forEach((element) => {
        if (hiddenData !== undefined) {
          if (hiddenData.some((x) => x === element.id)) {
            element.invalidData = true
          } else {
            element.invalidData = false
          }
        }
      })
    }
    return allHistoricalData
  }

  private cellRenderer({ columnIndex, key, rowIndex, style, parent }) {
    const { classes } = this.props
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const currentType: TelemetryType = Number(Object.keys(historicalData)[0])
    if (columnIndex === 0 && currentType !== TelemetryType.PumpControl) {
      return this.renderRowCellName(rowIndex, key, style, currentType)
    } else if (columnIndex === 0 && currentType === TelemetryType.PumpControl) {
      return this.renderCellNameMetadata(
        historicalData,
        currentType,
        columnIndex,
        rowIndex,
        classes,
        key,
        style
      )
    } else if (rowIndex === 0) {
      return this.renderHeaderCell(columnIndex, key, style)
    } else if (currentType === TelemetryType.TankLevel) {
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )
      const editRow = Object.keys(historicalData).length + 1

      if (rowIndex === editRow) {
        return this.editColumnTankCell(
          rowIndex,
          columnIndex,
          key,
          style,
          parent
        )
      }
      if (tank?.productionCode === ProductionCode.Chemical) {
        return this.renderCellNameChemicalTankMetadata(
          rowIndex,
          columnIndex,
          key,
          style
        )
      }
      return this.renderCellNameTankMetadata(rowIndex, columnIndex, key, style)
    } else if (currentType === TelemetryType.GasFlow) {
      return this.getDataRowGasFlow(
        historicalData,
        currentType,
        columnIndex,
        rowIndex,
        classes,
        key,
        style
      )
    } else if (currentType === TelemetryType.PumpControl) {
      return this.getDataRowMetadata(
        historicalData,
        currentType,
        columnIndex,
        rowIndex,
        classes,
        key,
        style
      )
    } else {
      const editRow = Object.keys(historicalData).length + 1
      const differentialRow = Object.keys(historicalData).length + 2

      if (rowIndex === editRow) {
        return this.editColumnCell(rowIndex, columnIndex, key, style, parent)
      }

      if (rowIndex === differentialRow) {
        return this.renderRowDifferentialValue(
          rowIndex,
          columnIndex,
          key,
          style
        )
      }

      return this.renderRowCellValue(rowIndex, columnIndex, key, style)
    }
  }

  private editColumnTankCell(
    rowIndex: any,
    columnIndex: any,
    key: any,
    style: any,
    parent
  ): React.ReactNode {
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)

    const rowData = data[columnIndex - 1]

    if (!rowData) {
      return null
    }

    const currData = rowData[rowIndex - 2]

    if (!currData) {
      return null
    }

    return (
      <React.Fragment>
        {this.showWindowValidData(key, style, currData, parent)}
      </React.Fragment>
    )
  }

  private editColumnCell(
    rowIndex: any,
    columnIndex: any,
    key: any,
    style: any,
    parent
  ): React.ReactNode {
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)

    const rowData = data[columnIndex - 1]

    if (!rowData) {
      return null
    }
    const currData =
      rowData[rowIndex - 2] ?? rowData.find((d) => d !== undefined)

    if (!currData) {
      return null
    }

    return (
      <React.Fragment>
        {this.showWindowValidData(key, style, currData, parent)}
      </React.Fragment>
    )
  }

  private renderRowDifferentialValue(
    rowIndex: number,
    columnIndex,
    key,
    style
  ): React.ReactNode {
    const { classes } = this.props
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)

    const rowData = data[columnIndex - 1]

    if (!rowData) {
      return null
    }
    const currData =
      rowData[rowIndex - 3] ?? rowData.find((d) => d !== undefined)

    if (!currData) {
      return null
    }

    const currentType: TelemetryType = Number(
      Object.keys(historicalData)[rowIndex - 3]
    )

    if (
      currentType === TelemetryType.CasingPressure ||
      currentType === TelemetryType.TubingPressure
    ) {
      if (currData.invalidData) {
        return (
          <div className={classes.cellInvalid} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {(currData.rateOfChangeValue ?? 0).toFixed(2)}
            </GreyTextTypography>
          </div>
        )
      } else {
        return (
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {(currData.rateOfChangeValue ?? 0).toFixed(2)}
            </GreyTextTypography>
          </div>
        )
      }
    }

    if (currData.invalidData) {
      return (
        <div className={classes.cellInvalid} key={key} style={style}>
          {(currData.rateOfChangeValue ?? 0).toFixed(2)}
        </div>
      )
    } else {
      return (
        <>
          <div className={classes.cell} key={key} style={style}>
            {(currData.rateOfChangeValue ?? 0).toFixed(2)}
          </div>
        </>
      )
    }
  }

  private readonly showWindowValidData = (
    key,
    style,
    currData,
    parent
  ): React.ReactNode => {
    const { classes } = this.props
    const { openModal } = parent.props
    let labelButton: string

    if (this.isAdmin) {
      if (currData.invalidData) {
        labelButton = 'Unhide'
        return (
          <div className={classes.cellInvalid} key={key} style={style}>
            <Button
              fullWidth={true}
              variant="contained"
              color="primary"
              onClick={() => openModal(currData)}
            >
              {labelButton}
            </Button>
          </div>
        )
      } else {
        labelButton = 'Hide'
        return (
          <div className={classes.cell} key={key} style={style}>
            <Button
              fullWidth={true}
              variant="contained"
              color="primary"
              onClick={() => openModal(currData)}
            >
              {labelButton}
            </Button>
          </div>
        )
      }
    }
    return null
  }

  private getDataRowMetadata(
    allHistoricalData,
    currentType,
    columnIndex,
    rowIndex,
    classes,
    key,
    style
  ) {
    let value = '-'
    const dataHistorical = allHistoricalData[currentType][columnIndex - 1]

    if (dataHistorical && dataHistorical.metaData) {
      const entries = Object.entries(dataHistorical.metaData)
      const metadataKey = Object.keys(dataHistorical.metaData)[rowIndex - 1] // rowIndex - 1 because rowIndex 0 is 'Time'

      if (metadataKey) {
        value = dataHistorical.metaData[metadataKey]
      }
    }

    return (
      <div className={classes.cell} key={key} style={style}>
        <Typography variant="caption" align="center">
          {value}
        </Typography>
      </div>
    )
  }

  private renderCellNameMetadata(
    allHistoricalData,
    currentType,
    columnIndex,
    rowIndex,
    classes,
    key,
    style
  ) {
    if (rowIndex === 0) {
      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            {'Time'}
          </GreyTextTypography>
        </div>
      )
    }

    const dataHistoricalArray = allHistoricalData[currentType]
    const lastHistoricalData =
      dataHistoricalArray[dataHistoricalArray.length - 1]

    if (lastHistoricalData && lastHistoricalData.metaData) {
      const entries = Object.keys(lastHistoricalData.metaData)
      const metadataKey = entries[rowIndex - 1] // rowIndex - 1 because rowIndex 0 is 'Time'

      if (metadataKey) {
        return (
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="caption" align="center">
              {metadataKey}
            </GreyTextTypography>
          </div>
        )
      }
    }
    return (
      <div className={classes.cell} key={key} style={style}>
        <GreyTextTypography variant="caption" align="center">
          -
        </GreyTextTypography>
      </div>
    )
  }

  private renderCellNameTankMetadata(
    rowIndex: number,
    columnIndex,
    key,
    style
  ) {
    const { classes } = this.props
    try {
      if (rowIndex === 0) {
        return (
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {'Time'}
            </GreyTextTypography>
          </div>
        )
      }

      const historicalData = this.hideHistoricalData(
        this.props.allHistoricalData,
        this.state.hiddenData
      )
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )

      const data = this.memoizedData(historicalData)

      let rowData = data[columnIndex - 1]
      const currIndex = rowIndex - 1
      if (!rowData) {
        return
      }
      if (!rowData) {
        return
      }
      let currData = rowData[rowIndex - 1]
      let currentType: TelemetryType = Number(
        Object.keys(historicalData)[rowIndex - 1]
      )
      if (currData === undefined) {
        let value = ''
        if (currIndex === 2) {
          currentType = Number(Object.keys(historicalData)[rowIndex - 3])
          rowData = data[columnIndex - 1]

          if (!rowData) {
            return
          }
          currData = rowData[rowIndex - 3]

          const differentialData = currData.rateOfChangeValue ?? 0

          if (tank?.productionCode === ProductionCode.Chemical) {
            value = this.getRowChemicalTankCellValue(differentialData, tank)
          } else {
            value = this.getRowTankCellValue(differentialData, true, tank)
          }

          return (
            <div className={classes.cell} key={key} style={style}>
              <GreyTextTypography variant="subtitle2" align="center">
                {value}
              </GreyTextTypography>
            </div>
          )
        }
        if (currIndex === 3) {
          currentType = Number(Object.keys(historicalData)[rowIndex - 3])
          if (!rowData) {
            return
          }
          currData = rowData[rowIndex - 4]

          const differentialData = currData.rateOfChangeValue ?? 0

          if (tank?.productionCode === ProductionCode.Chemical) {
            value = this.getRowChemicalTankCellValue(differentialData, tank)
          } else {
            value = this.getRowTankCellValue(differentialData)
          }

          return (
            <div className={classes.cell} key={key} style={style}>
              <GreyTextTypography variant="subtitle2" align="center">
                {value}
              </GreyTextTypography>
            </div>
          )
        }
      }
      const value: string = this.getRowCellValue(currentType, currData.value)
      const bblValue = this.getRowCellValue(
        currentType,
        currData.value,
        true,
        tank
      )

      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            {`${bblValue} bbl`}
          </GreyTextTypography>
          <GreyTextTypography variant="subtitle2" align="center">
            {value}
          </GreyTextTypography>
        </div>
      )
    } catch {
      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            -
          </GreyTextTypography>
        </div>
      )
    }
  }

  private renderCellNameChemicalTankMetadata(
    rowIndex: number,
    columnIndex,
    key,
    style
  ) {
    const { classes } = this.props
    try {
      if (rowIndex === 0) {
        return (
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {'Time'}
            </GreyTextTypography>
          </div>
        )
      }

      const historicalData = this.hideHistoricalData(
        this.props.allHistoricalData,
        this.state.hiddenData
      )
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )

      const data = this.memoizedData(historicalData)

      let rowData = data[columnIndex - 1]
      const currIndex = rowIndex - 1
      if (!rowData) {
        return
      }
      if (!rowData) {
        return
      }
      let currData = rowData[rowIndex - 1]
      let currentType: TelemetryType = Number(
        Object.keys(historicalData)[rowIndex - 1]
      )
      if (currData === undefined) {
        let value = ''
        if (currIndex === 2) {
          currentType = Number(Object.keys(historicalData)[rowIndex - 3])
          rowData = data[columnIndex - 1]

          if (!rowData) {
            return
          }
          currData = rowData[rowIndex - 3]

          const differentialData = currData.rateOfChangeValue ?? 0

          if (tank?.productionCode === ProductionCode.Chemical) {
            const volume = differentialData ?? 0
            value = this.getRowChemicalTankCellValue(volume, tank)
          } else {
            value = this.getRowTankCellValue(differentialData, true, tank)
          }

          return (
            <div className={classes.cell} key={key} style={style}>
              <GreyTextTypography variant="subtitle2" align="center">
                {value}
              </GreyTextTypography>
            </div>
          )
        }
      }
      const value: string = this.getRowCellValue(
        currentType,
        currData.value,
        false,
        tank
      )

      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            {value}
          </GreyTextTypography>
        </div>
      )
    } catch {
      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            -
          </GreyTextTypography>
        </div>
      )
    }
  }

  private getDataRowGasFlow(
    allHistoricalData,
    currentType,
    columnIndex,
    rowIndex,
    classes,
    key,
    style
  ) {
    let value
    const dataHistorical = allHistoricalData[currentType][columnIndex - 1]

    switch (rowIndex - 1) {
      case 0:
        value = dataHistorical.values.AccumulatedVolume ?? '0'
        break
      case 1:
        value = dataHistorical.values.BatteryVolts ?? '0'
        break
      case 2:
        value = dataHistorical.values.DiffPressure ?? '0'
        break
      case 3:
        value = dataHistorical.values.StaticPressure ?? '0'
        break
      case 4:
        value = dataHistorical.values.Temperature ?? '0'
        break
      case 5:
        value = dataHistorical.values.TodayVolumeFlowed ?? '0'
        break
      case 6:
        value = dataHistorical.values.VolumeFlowRate ?? '0'
        break
      case 7:
        value = dataHistorical.values.YesterdayVolumeFlowed ?? '0'
        break
      default:
        value = ''
        break
    }

    return (
      <div className={classes.cell} key={key} style={style}>
        {isNaN(+value) ? ' - ' : (+value).toFixed(2)}
      </div>
    )
  }

  private noContentRenderer() {
    return <div className={this.props.classes.noCells}>No data available</div>
  }

  private getColumnWidth(currentType: TelemetryType, { index }) {
    if (currentType === TelemetryType.PumpControl) {
      switch (index) {
        case 0:
          return 140
        default:
          return 85 * 2
      }
    } else {
      switch (index) {
        case 0:
          return 140
        default:
          return 85
      }
    }
  }

  private renderHeaderCell(index: number, key, style): React.ReactNode {
    const { classes } = this.props
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)
    const currData = data[index - 1]
    const date = new Date(currData!.filter((t) => t.timestamp)[0]?.timestamp)

    return (
      <div className={classes.cell} key={key} style={style}>
        <GreyTextTypography variant="subtitle2" align="center">
          {this.getFormattedDate(date)}
        </GreyTextTypography>
      </div>
    )
  }

  private renderRowCellValue(
    rowIndex: number,
    columnIndex,
    key,
    style
  ): React.ReactNode {
    const { classes } = this.props
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    const data = this.memoizedData(historicalData)
    const rowData = data[columnIndex - 1]

    if (!rowData) {
      return null
    }
    const currData = rowData[rowIndex - 1]

    if (!currData) {
      return (
        <div className={classes.cell} key={key} style={style}>
          N/A
        </div>
      )
    }
    const currentType: TelemetryType = Number(
      Object.keys(historicalData)[rowIndex - 1]
    )

    const value: string = this.getRowCellValue(currentType, currData.value)

    if (
      currentType === TelemetryType.CasingPressure ||
      currentType === TelemetryType.TubingPressure
    ) {
      if (currData.invalidData) {
        return (
          <div className={classes.cellInvalid} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {value}
            </GreyTextTypography>
            <GreyTextTypography variant="caption" align="center">
              {currData.unitOfMeasure}
            </GreyTextTypography>
          </div>
        )
      } else {
        return (
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {value}
            </GreyTextTypography>
            <GreyTextTypography variant="caption" align="center">
              {currData.unitOfMeasure}
            </GreyTextTypography>
          </div>
        )
      }
    }

    if (currData.invalidData) {
      return (
        <div className={classes.cellInvalid} key={key} style={style}>
          {value}
        </div>
      )
    } else {
      return (
        <>
          <div className={classes.cell} key={key} style={style}>
            {value}
          </div>
        </>
      )
    }
  }

  private getRowCellValue(
    currentType: TelemetryType,
    value,
    isBBL = false,
    tank?: Tank
  ): string {
    switch (currentType) {
      case TelemetryType.StaticTemp:
        return celsiusToFahrenheit(value).toFixed(2)

      case TelemetryType.TankLevel:
        if (isBBL) {
          if (!tank) {
            return ''
          }

          const volume = feetToBbl(value, tank?.height, tank?.capacity).toFixed(
            1
          )
          return volume
        } else {
          if (tank) {
            if (tank.productionCode === ProductionCode.Chemical) {
              return value.toFixed(2)
            }
          }
          const [tankLevelFt, tankLevelIn] = feetToFeetAndInches(value)
          return `${tankLevelFt.toFixed()}' ${tankLevelIn.toFixed(1)}"`
        }
      default:
        return value.toFixed(2)
    }
  }

  private getRowChemicalTankCellValue(value, tank?: Tank): string {
    if (!tank) {
      return ''
    }
    return value.toFixed(2)
  }

  private getRowTankCellValue(value, isBBL = false, tank?: Tank): string {
    if (isBBL) {
      if (!tank) {
        return ''
      }
      const feet = inchToFeet(value)
      const volume = feetToBbl(feet, tank?.height, tank?.capacity).toFixed(2)
      return volume
    } else {
      const feet = inchToFeet(value)
      const [tankLevelFt, tankLevelIn] = feetToFeetAndInches(feet)
      return `${tankLevelFt.toFixed()}' ${tankLevelIn.toFixed(1)}"`
    }
  }

  private renderRowCellName(
    index: number,
    key,
    style,
    parentCurrentType: TelemetryType
  ): React.ReactNode {
    const { classes } = this.props
    const historicalData = this.hideHistoricalData(
      this.props.allHistoricalData,
      this.state.hiddenData
    )
    if (index === 0) {
      return (
        <div className={classes.cell} key={key} style={style}>
          <GreyTextTypography variant="subtitle2" align="center">
            {'Time'}
          </GreyTextTypography>
        </div>
      )
    }
    let lastData = Object.keys(historicalData).length

    const currData = index - 1

    const currentType: TelemetryType = Number(
      Object.keys(historicalData)[index - 1]
    )

    let rowName, rowUnits
    if (parentCurrentType === TelemetryType.GasFlow) {
      rowName = rowNamesGasFlow[index - 1]
      lastData = rowNamesGasFlow.length
      rowUnits = this.getTypeUnits(parentCurrentType, rowName)
    } else if (parentCurrentType === TelemetryType.TankLevel) {
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )

      if (tank?.productionCode === ProductionCode.Chemical) {
        rowName = rowNamesChemicalTankLevel[index - 1]
        lastData = rowNamesChemicalTankLevel.length
        rowUnits = ''
      } else {
        rowName = rowNamesTankLevel[index - 1]
        lastData = rowNamesTankLevel.length
        rowUnits = ''
      }
    } else {
      rowName = getReadableTelemetryType(currentType)
      rowUnits = this.getTypeUnits(currentType)
    }

    if (lastData === currData) {
      return (
        <>
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {'Edit'}
            </GreyTextTypography>
          </div>
        </>
      )
    }
    if (lastData + 1 === currData) {
      return (
        <>
          <div className={classes.cell} key={key} style={style}>
            <GreyTextTypography variant="subtitle2" align="center">
              {'Rate Of Change'}
            </GreyTextTypography>
          </div>
        </>
      )
    }

    return (
      <div className={classes.cell} key={key} style={style}>
        <GreyTextTypography variant="subtitle2" align="center">
          {rowName}
        </GreyTextTypography>
        <GreyTextTypography variant="caption" align="center">
          {rowUnits}
        </GreyTextTypography>
      </div>
    )
  }

  private getTypeUnits(rowType: TelemetryType, labelName?: string): string {
    let rowUnits: string
    switch (rowType) {
      case TelemetryType.CasingPressure:
      case TelemetryType.TubingPressure:
        rowUnits = ''
        break
      case TelemetryType.TankLevel:
        rowUnits = 'feet'
        break
      case TelemetryType.GasFlow:
        switch (labelName) {
          case 'Diff Pressure':
            rowUnits = 'PSIG'
            break
          case 'Static Pressure':
            rowUnits = 'PSIG'
            break
          case 'Temperature':
            rowUnits = 'F°'
            break
          case 'Battery Volts':
            rowUnits = 'V'
            break
          default:
            rowUnits = 'MCF'
        }

        break
      default:
        rowUnits = getTelemetryTypeUnit(rowType)
        break
    }
    return rowUnits
  }

  private getFormattedDate(date: Date): string {
    const locale = window.navigator.language
    moment.locale(locale)

    const localeData = moment.localeData()
    let format = localeData.longDateFormat('llll')
    format = format.replace(/YYYY/, '').replace(/ddd, /, '')

    return moment(date).format(format)
  }
}

const mapPropsFromState = ({ oidc, multitenantUser }: AppState) => ({
  accessToken: multitenantUser.accessToken,
  tenantId: multitenantUser.tenants?.find((t) => t.selected)?.id || 0,
  email: multitenantUser.email,
  user: multitenantUser,
})

export default connect(mapPropsFromState)(withStyles(styles)(MeasuresTable))
